import { useState, type DragEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  KanbanBoard as Board,
  KanbanCard as Card,
  StageConfigurationView,
  WorkflowStage,
} from '@tailoring-crm/shared';
import { cn } from '@tailoring-crm/ui';
import { KanbanCardTile } from './kanban-card.js';

/**
 * The columns.
 *
 * They scroll sideways rather than wrapping, and each column is a fixed, readable width, so the
 * board looks the same on a 360px phone as on a laptop — one column at a time on the phone,
 * five side by side on the laptop. Nothing here depends on the viewport being wide.
 *
 * Drag and drop is native HTML5 and is deliberately an ENHANCEMENT: the drop target sets the
 * same handler the per-card stage control calls, so removing dragging entirely would cost the
 * board no capability. That is the right relationship, because dragging between horizontally
 * scrolled columns on a phone does not work, and the shop uses phones.
 *
 * THE PAGE DOES NOT GROW WITH THE BOARD. At `md` and up the board fills the window (the board page
 * asks the shell for it through `data-fills-viewport`), every column stretches to the bottom, and
 * only the cards between a column's header and its "+N more" footer scroll. Phones keep the page
 * scroll: a column there is most of the screen wide, and a locked strip would be too short to read.
 */
export interface KanbanBoardProps {
  board: Board;
  configuration: StageConfigurationView | undefined;
  selected: ReadonlySet<string>;
  onToggleSelected: (itemId: string) => void;
  onMove: (card: Card, toStage: WorkflowStage) => void;
  onRevert: (card: Card, toStage: WorkflowStage) => void;
  /** Omitted for a caller who may not hand work out; cards then carry no assign control. */
  onAssign?: (card: Card) => void;
  canRevert?: boolean;
  movingItemId?: string | null;
}

export function KanbanBoardView({
  board,
  configuration,
  selected,
  onToggleSelected,
  onMove,
  onRevert,
  onAssign,
  canRevert = true,
  movingItemId = null,
}: KanbanBoardProps): ReactElement {
  const { t } = useTranslation();
  const [dropTarget, setDropTarget] = useState<WorkflowStage | null>(null);

  const cardsById = new Map(
    board.columns.flatMap((column) => column.cards.map((card) => [card.itemId, card] as const)),
  );

  const onDrop = (stage: WorkflowStage) => (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDropTarget(null);
    const itemId = event.dataTransfer.getData('text/plain');
    const card = cardsById.get(itemId);
    if (!card || card.stage === stage) return;
    // Forward or backward is the caller's decision to route: a backward drop must still ask
    // for a reason, so it goes through the same door the menu uses.
    onMove(card, stage);
  };

  return (
    <div
      className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-2 md:min-h-0 md:flex-1"
      role="group"
      aria-label={t('kanban.title')}
    >
      {board.columns.map((column) => (
        // A named <section> is a landmark region, so a screen reader can jump between columns
        // — which on a phone, where only one column is on screen at a time, is how the board
        // is navigated at all.
        <section
          key={column.stage}
          aria-label={column.label}
          onDragOver={(event) => {
            event.preventDefault();
            setDropTarget(column.stage);
          }}
          onDragLeave={() =>
            setDropTarget((current) => (current === column.stage ? null : current))
          }
          onDrop={onDrop(column.stage)}
          className={cn(
            'flex w-[85vw] shrink-0 flex-col gap-2 rounded-lg border border-border border-t-4 bg-card p-2 sm:w-72 md:min-h-0',
            // The top edge says what kind of stage this is before the label is read: open work
            // is blue, a finished piece is green, a cancelled one is red.
            column.stage === 'cancelled'
              ? 'border-t-stage-lost'
              : column.stage === 'ready' || column.stage === 'delivered'
                ? 'border-t-stage-done'
                : 'border-t-stage-open',
            dropTarget === column.stage && 'ring-2 ring-ring',
          )}
        >
          <header className="flex shrink-0 items-center justify-between gap-2 px-1">
            <h3 className="truncate text-sm font-semibold">{column.label}</h3>
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold tabular-nums">
              {column.total}
            </span>
          </header>

          {column.cards.length === 0 ? (
            <p className="px-1 py-6 text-center text-sm text-muted-foreground">
              {t('kanban.columnEmpty')}
            </p>
          ) : (
            <ul className="kanban-cards flex flex-col gap-2 md:-mr-1 md:min-h-0 md:flex-1 md:overflow-y-auto md:pr-1">
              {column.cards.map((card) => (
                <KanbanCardTile
                  key={card.itemId}
                  card={card}
                  configuration={configuration}
                  selected={selected.has(card.itemId)}
                  onToggleSelected={onToggleSelected}
                  onAdvance={onMove}
                  onRevert={onRevert}
                  {...(onAssign ? { onAssign } : {})}
                  canRevert={canRevert}
                  busy={movingItemId === card.itemId}
                />
              ))}
            </ul>
          )}

          {/* A capped column says what it is not showing rather than pretending to be short. */}
          {column.total > column.cards.length ? (
            <p className="shrink-0 px-1 pb-1 text-center text-xs text-muted-foreground">
              {t('kanban.moreHidden', { count: column.total - column.cards.length })}
            </p>
          ) : null}
        </section>
      ))}
    </div>
  );
}
